import { useState } from "react";
import type {
  JobReportEvent,
  JobReportAttachment,
} from "@/domain/model/JobReport";

// MUI Timeline Imports (Standard MUI v7 setup)
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent,
} from "@mui/lab";
import { Typography, Paper, Box, Stack, ButtonBase } from "@mui/material";

// Icons for different event types
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import PauseIcon from "@mui/icons-material/Pause";
import StopIcon from "@mui/icons-material/Stop";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import DrawIcon from "@mui/icons-material/Draw";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";

import Lightbox from "yet-another-react-lightbox";
import "yet-another-react-lightbox/styles.css";
import type { TimelineEvent } from "./JobTimelineTypes";

interface JobTimelineProps {
  events: TimelineEvent[];
}

export function JobTimeline({ events }: JobTimelineProps) {
  const [open, setOpen] = useState(false);
  const [slides, setSlides] = useState<{ src: string }[]>([]);
  const [index, setIndex] = useState(0);
  // Sort events chronologically just in case the server didn't
  const sortedEvents = [...events].sort(
    (a, b) => a.occurredAt.getTime() - b.occurredAt.getTime(),
  );

  const openLightbox = (
    attachments: JobReportAttachment[],
    currentId: string,
  ) => {
    const allImages = attachments.map((a) => ({ src: a.downloadUrl }));
    const currentIndex = attachments.findIndex(
      (a) => a.attachmentId === currentId,
    );
    setSlides(allImages);
    setIndex(currentIndex);
    setOpen(true);
  };

  // Helper to pick the right icon and color based on event type
  const getEventConfig = (type: JobReportEvent["type"]) => {
    switch (type) {
      case "JOB_START":
      case "JOB_RESUME":
        return {
          icon: <PlayArrowIcon />,
          color: "success" as const,
          label: "Gestartet",
        };
      case "JOB_PAUSE":
        return {
          icon: <PauseIcon />,
          color: "warning" as const,
          label: "Pausiert",
        };
      case "JOB_SUSPEND":
        return {
          icon: <ErrorOutlineIcon />,
          color: "error" as const,
          label: "Unterbrochen",
        };
      case "JOB_CLOSE_DAY":
        return {
          icon: <StopIcon />,
          color: "info" as const,
          label: "Feierabend",
        };
      case "JOB_COMPLETE":
        return {
          icon: <CheckBoxIcon />,
          color: "success" as const,
          label: "Abgeschlossen",
        };
      case "CREW_JOIN":
        return {
          icon: <PersonAddIcon />,
          color: "primary" as const,
          label: "Mitarbeiter hinzugefügt",
        };
      case "CREW_LEAVE":
        return {
          icon: <PersonRemoveIcon />,
          color: "grey" as const,
          label: "Mitarbeiter abgemeldet",
        };
      case "OBS_PHOTO":
        return {
          icon: <PhotoCameraIcon />,
          color: "primary" as const,
          label: "Foto hinzugefügt",
        };
      case "OBS_NOTE":
        return {
          icon: <NoteAltIcon />,
          color: "primary" as const,
          label: "Notiz erstellt",
        };
      case "OBS_SIGNATURE":
        return {
          icon: <DrawIcon />,
          color: "secondary" as const,
          label: "Unterschrift erfasst",
        };
      default:
        return {
          icon: <NoteAltIcon />,
          color: "grey" as const,
          label: "Ereignis",
        };
    }
  };

  // Format time (e.g., "08:30")
  const formatTime = (date: Date) => {
    return new Intl.DateTimeFormat("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  if (sortedEvents.length === 0) {
    return (
      <Typography color="text.secondary">
        Keine Ereignisse aufgezeichnet.
      </Typography>
    );
  }

  return (
    <>
      <Timeline position="right" sx={{ p: 0, m: 0 }}>
        {sortedEvents.map((event, idx) => {
          const isLast = idx === sortedEvents.length - 1;
          const config = getEventConfig(event.type);
          const hasPhotos = event.attachments.length > 0;

          return (
            <TimelineItem key={event.eventId}>
              <TimelineOppositeContent sx={{ flex: 0.15, pl: 0, py: 1.5 }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  fontWeight="700"
                >
                  {formatTime(event.occurredAt)}
                </Typography>
              </TimelineOppositeContent>

              <TimelineSeparator>
                <TimelineDot
                  color={config.color}
                  variant={hasPhotos ? "filled" : "outlined"}
                >
                  {config.icon}
                </TimelineDot>
                {!isLast && <TimelineConnector />}
              </TimelineSeparator>

              <TimelineContent sx={{ py: 1.5, px: 2 }}>
                <Paper
                  elevation={0}
                  variant="outlined"
                  sx={{ p: 2, borderRadius: 2 }}
                >
                  <Stack spacing={1.5}>
                    <Box>
                      <Typography variant="subtitle2" fontWeight="800">
                        {config.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        von {event.actor.displayName}
                      </Typography>
                    </Box>

                    {/* Dynamic Payloads */}
                    {event.data?.obsNote && (
                      <Typography variant="body2">
                        "{event.data.obsNote.text}"
                      </Typography>
                    )}
                    {event.data?.obsPhoto?.caption && (
                      <Typography variant="body2" color="text.secondary">
                        {event.data.obsPhoto.caption}
                      </Typography>
                    )}
                    {event.data?.obsSignature && (
                      <Box
                        sx={{
                          bgcolor: "#f9f9f9",
                          p: 1,
                          borderRadius: 1,
                          border: "1px dashed #ccc",
                          width: "fit-content",
                        }}
                      >
                        <Typography variant="caption" display="block">
                           Unterschrieben von {event.data.obsSignature.signerName}
                        </Typography>
                      </Box>
                    )}

                    {/* Image Gallery Implementation */}
                    {hasPhotos && (
                      <Box
                        display="flex"
                        flexWrap="wrap"
                        gap={1.5}
                        sx={{ mt: 1 }}
                      >
                        {event.attachments.map((att) => (
                          <ButtonBase
                            key={att.attachmentId}
                            onClick={() =>
                              openLightbox(event.attachments, att.attachmentId)
                            }
                            sx={{
                              width: 110, // Slightly larger for better visibility
                              height: 110,
                              borderRadius: 1.5, // Match MUI Paper rounding
                              overflow: "hidden",
                              bgcolor: "grey.100", // "High-quality" neutral background
                              border: "1px solid",
                              borderColor: "divider",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s ease-in-out",
                              "&:hover": {
                                borderColor: "primary.main",
                                transform: "translateY(-2px)",
                                boxShadow: 1,
                              },
                            }}
                          >
                            <img
                              src={att.downloadUrl}
                              loading="lazy"
                              alt={att.filename ?? "Anhang"}
                              style={{
                                maxWidth: "100%",
                                maxHeight: "100%",
                                objectFit: "contain", // Essential: Never crop technical proof
                                display: "block",
                              }}
                            />
                          </ButtonBase>
                        ))}
                      </Box>
                    )}
                  </Stack>
                </Paper>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>

      <Lightbox
        open={open}
        close={() => setOpen(false)}
        index={index}
        slides={slides}
        // Basic configuration for stability/minimalism
        carousel={{ finite: slides.length <= 1 }}
        render={{ iconPrev: () => null, iconNext: () => null }} // Clean look if only 1
      />
    </>
  );
}
